// store/notifications.js
import { defineStore } from 'pinia';
import { useUserStore } from '@store/user';
import { toast } from 'vue-sonner';
import Tinycon from 'tinycon';
import { h } from 'vue';
import router from '@/router';
import { buildNotificationRoute } from '@utilities/notificationActions';

let tinyconInitialized = false;
let focusListenerSetup = false;
import IncomingCall from '@components/notifications/IncomingCall.vue';

export const useNotificationsStore = defineStore('notifications', {
    state: () => {
        return {
            loading: false,
            all: [],
            unreadCount: 0,
            listener: null,
            listenerChannel: null,
            listenerUserId: null,
            incomingCallListener: null,
            incomingCallUserListener: null,
            incomingCallToasts: {}, // Map of inbox_call_id to toast ID
            inactive_status_code: useStorage('inactive_status_code', null),
            inactive_status: useStorage('inactive_status', null),
            inactive_dismissible: useStorage('inactive_dismissible', false),
        };
    },
    actions: {
        updateFaviconBadge() {
            // Initialize tinycon on first use
            if (typeof window !== 'undefined' && !tinyconInitialized) {
                Tinycon.setOptions({
                    width: 7,
                    height: 9,
                    font: window.devicePixelRatio && window.devicePixelRatio > 1 ? '18px arial' : '10px arial',
                    color: '#ffffff',
                    background: '#ef4444',
                    fallback: true,
                    abbreviate: true,
                });
                tinyconInitialized = true;
            }

            if (this.unreadCount > 0) {
                Tinycon.setBubble(this.unreadCount);
            } else {
                Tinycon.reset();
            }
        },
        add(notification) {
            const toastOptions = { position: 'top-center' };

            const route = buildNotificationRoute(notification);
            if (route) {
                toastOptions.action = {
                    label: 'View order',
                    onClick: () => router.push(route),
                };
            }

            toast(notification.data.message, toastOptions);
            this.all = [notification, ...this.all];
            // Increment unread count for new notifications
            if (!notification.read_at) {
                this.unreadCount += 1;
                this.updateFaviconBadge();
            }
        },

        async retrieve() {
            this.loading = true;
            const response = await window.tenantAxios.get('me/notifications');
            this.all = response.data.data;
            await this.fetchUnreadCount();
            this.loading = false;
        },

        async fetchUnreadCount() {
            const response = await window.tenantAxios.get('me/notifications/unread-count');
            this.unreadCount = response.data.count;
            this.updateFaviconBadge();
        },

        /*
         * NotificationIds are comma separated string of uuids for the notifications to be modified
         */
        async read(notificationIds) {
            this.loading = true;

            await window.tenantAxios.post('me/notifications/read', {
                ids: notificationIds,
            });

            await this.retrieve();
            this.updateFaviconBadge();

            this.loading = false;
        },

        /*
         * Optimistically mark notification as read without refetching
         * Used when clicking notifications to avoid re-render race conditions
         */
        markAsReadOptimistic(notificationId) {
            const notification = this.all.find((n) => n.id === notificationId);
            if (notification && !notification.read_at) {
                notification.read_at = new Date().toISOString();
                this.unreadCount = Math.max(0, this.unreadCount - 1);
                this.updateFaviconBadge();

                // Send API request in background without awaiting
                window.tenantAxios
                    .post('me/notifications/read', {
                        ids: notificationId,
                    })
                    .catch((error) => {
                        console.error('Error marking notification as read:', error);
                        // Optionally revert optimistic update on error
                    });
            }
        },

        async markAllRead() {
            this.loading = true;

            await window.tenantAxios.post('me/notifications/mark-all-read');

            await this.retrieve();
            this.updateFaviconBadge();
            this.loading = false;
        },

        stopListener() {
            if (this.listenerChannel && window.echo) {
                window.echo.leave(this.listenerChannel);
            }

            this.listener = null;
            this.listenerChannel = null;
            this.listenerUserId = null;
        },

        startListener() {
            const user = useUserStore();
            const userId = user.getUser?.id;

            if (!window.echo || !userId) {
                return;
            }

            const listenerChannel = `App.Models.User.${userId}`;

            if (this.listenerUserId === userId && this.listener) {
                return;
            }

            this.stopListener();

            this.listenerUserId = userId;
            this.listenerChannel = listenerChannel;
            this.listener = window.echo.private(listenerChannel).notification((notification) => {
                if (notification.type === 'App\\Notifications\\UserActivityNotification') {
                    if (user.isMoveproSuperAdminUser) {
                        return;
                    }

                    this.inactive_status_code = notification.status;
                    this.inactive_status = notification.message;
                    this.inactive_dismissible = notification.dismissible;

                    if (!notification.dismissible) {
                        user.activity_status = notification.status;
                    }

                    return;
                }

                this.add({
                    // Preserve all notification data fields (action, action_params, etc.)
                    data: notification,
                    id: notification.id,
                    type: notification.type,
                    read_at: null,
                });
            });

            // Set up window focus listener to refresh unread count and update favicon
            if (typeof window !== 'undefined' && !focusListenerSetup) {
                window.addEventListener('focus', this.handleWindowFocus.bind(this));
                focusListenerSetup = true;
            }

            // Set up incoming call notifications
            this.startIncomingCallListener();
        },

        handleWindowFocus() {
            // Clear the favicon badge when the tab regains focus
            if (typeof window !== 'undefined' && tinyconInitialized) {
                Tinycon.reset();
            }
        },

        startIncomingCallListener() {
            const user = useUserStore();

            if (!window.echo || !user.tenantId || !user.getUser?.id) {
                return;
            }

            // Clean up existing listeners if any
            if (this.incomingCallListener) {
                window.echo.leave(`call.tenant.${user.tenantId}`);
                this.incomingCallListener = null;
            }

            if (this.incomingCallUserListener) {
                window.echo.leave(`call.user.${user.getUser.id}`);
                this.incomingCallUserListener = null;
            }

            // Handler for incoming call events (used by both channels)
            const handleIncomingCallEvent = (event) => {
                // Don't show notification for movepro super users
                if (user.isMoveproSuperAdminUser) {
                    return;
                }

                // Store toast ID keyed by inbox_call_id for later dismissal
                const key = `inbox_call_${event.inbox_call_id}`;

                let dismissFunction = null;
                const toastId = toast.custom(
                    (t) => {
                        // Store the dismiss function - this should work reliably
                        dismissFunction = t.dismiss;

                        return h(IncomingCall, {
                            phoneNumber: event.phone_number,
                            customerName: event.customer_name,
                            orderId: event.order_id,
                            orderRef: event.order_ref,
                            moveType: event.move_type,
                            inboxCallId: event.inbox_call_id, // Pass inbox_call_id so component can look up toastId
                        });
                    },
                    {
                        duration: 30000, // 30 seconds
                    },
                );

                // Store the dismiss function and toastId for dismissal
                this.incomingCallToasts[key] = {
                    dismiss: dismissFunction,
                    id: toastId,
                };
            };

            // Handler for call hungup events (used by both channels)
            const handleIncomingCallHungupEvent = (event) => {
                // Dismiss the toast for this call
                const key = `inbox_call_${event.inbox_call_id}`;
                const toastData = this.incomingCallToasts[key];

                if (toastData) {
                    // Try dismiss function first (most reliable)
                    if (toastData.dismiss && typeof toastData.dismiss === 'function') {
                        toastData.dismiss();
                    } else if (toastData.id !== null && toastData.id !== undefined) {
                        toast.dismiss(toastData.id);
                    } else if (typeof toastData === 'string' || typeof toastData === 'number') {
                        // Handle legacy format
                        toast.dismiss(toastData);
                    }
                    delete this.incomingCallToasts[key];
                }
            };

            // Listen to tenant-wide channel for unassigned calls
            this.incomingCallListener = window.echo
                .private(`call.tenant.${user.tenantId}`)
                .listen('IncomingCallEvent', handleIncomingCallEvent)
                .listen('IncomingCallHungupEvent', handleIncomingCallHungupEvent);

            // Listen to user-specific channel for assigned calls
            this.incomingCallUserListener = window.echo
                .private(`call.user.${user.getUser.id}`)
                .listen('IncomingCallEvent', handleIncomingCallEvent)
                .listen('IncomingCallHungupEvent', handleIncomingCallHungupEvent);
        },
    },
    getters: {
        getUnreadCount: (state) => {
            return state.unreadCount;
        },

        // Keep the old getter for backward compatibility (calculates from loaded notifications)
        getUnreadCountFromLoaded: (state) => {
            return state.all.reduce((accumulator, currentValue) => {
                if (!currentValue.read_at) {
                    accumulator = accumulator + 1;
                }
                return accumulator;
            }, 0);
        },

        getAllCount: (state) => {
            if (state.all && state.all.length) {
                return state.all.length;
            }
        },

        getUnread: (state) => {
            return state.unread;
        },

        getAll: (state) => {
            return state.all;
        },
    },
});
